// src/App.tsx
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './components/Login';
import Register from './components/Register';
import ResetPassword from './components/ResetPassword';
import Dashboard from './components/Dashboard';
import AdminDashboard from './components/AdminDashboard';
import TechnicalAdminDashboard from './components/TechnicalAdminDashboard';
import AddTradingAccount from './components/AddTradingAccount';
import HomePage from './components/HomePage';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import AccountDetails from './components/trading/AccountDetails';
import { logFirebaseEnvironment } from './utils/firebaseHelpers';
import Layout from './components/Layout';
import NotFound from './components/NotFound';
import TermsAndConditions from './components/TermsAndConditions';
import SettingsView from './components/admin/SettingsView';

// Protected route component
interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { currentUser, loading } = useAuth();
  
  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }
  
  return currentUser ? <>{children}</> : <Navigate to="/" />;
};

// Technical Admin Route - checks for both admin and technical admin roles
interface TechnicalAdminRouteProps {
  children: React.ReactNode;
}

const TechnicalAdminRoute = ({ children }: TechnicalAdminRouteProps) => {
  const { currentUser, userData, loading } = useAuth();
  
  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }
  
  // Check if user is authenticated and is a technical admin
  if (!currentUser || !userData?.isAdmin || !userData?.isTechnicalAdmin) {
    return <Navigate to="/admin" />;
  }
  
  return <>{children}</>;
};

const App: React.FC = () => {
  // Log Firebase environment information on app startup
  useEffect(() => {
    logFirebaseEnvironment();
  }, []);

  return (
    <AuthProvider>
      <Router>
        <Layout>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/admin" element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="/admin/settings" element={
              <TechnicalAdminRoute>
                <SettingsView />
              </TechnicalAdminRoute>
            } />
            <Route path="/admin/technical" element={
              <TechnicalAdminRoute>
                <TechnicalAdminDashboard />
              </TechnicalAdminRoute>
            } />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/terms" element={<TermsAndConditions />} />
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/add-trading-account" 
              element={
                <ProtectedRoute>
                  <AddTradingAccount />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/account-details/:accountId" 
              element={
                <ProtectedRoute>
                  <AccountDetails />
                </ProtectedRoute>
              } 
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Layout>
      </Router>
    </AuthProvider>
  );
};

export default App;